// Bold Maximalism landing page — monolithic iframe block
// 4 brand variants: brutal / luxe / acid / candy
// Uses body[data-theme] attribute pattern

export type MaximalLandingBrand = 'brutal' | 'luxe' | 'acid' | 'candy'
export interface MaximalLandingPageData { brand: MaximalLandingBrand }
export const DEFAULT_MAXIMAL_LANDING_DATA: MaximalLandingPageData = { brand: 'brutal' }

// ── CSS ───────────────────────────────────────────────────────────────────────
const MAXIMAL_LANDING_CSS = `
/* ============ БАЗА ============ */
*,*::before,*::after{margin:0;padding:0;box-sizing:border-box}
:root{--wrap:1440px;--pad:clamp(16px,4vw,48px)}
html{scroll-behavior:smooth;scroll-padding-top:96px}
body{
  --bg:#F2F0EA; --ink:#141414; --paper:#ffffff; --accent:#FFD500; --accent2:#FF3B00;
  --f-display:'Archivo Black',sans-serif; --f-body:'Space Grotesk',sans-serif; --f-mono:'IBM Plex Mono',monospace;
  --bd:3px; --rad:0px; --shadow:8px 8px 0 var(--ink); --noise-op:.07;
  background:var(--bg); color:var(--ink); font-family:var(--f-body);
  overflow-x:hidden; transition:background .5s ease,color .5s ease;
}
body[data-theme="luxe"]{
  --bg:#F6F1E7; --ink:#1C1917; --paper:#FFFDF7; --accent:#B08D3E; --accent2:#1F3A2D;
  --f-display:'Cormorant Garamond',serif; --f-body:'Space Grotesk',sans-serif;
  --bd:1px; --rad:0px; --shadow:0 24px 60px rgba(28,25,23,.14); --noise-op:.05;
}
body[data-theme="acid"]{
  --bg:#0A0A0F; --ink:#EAF6E0; --paper:#12121A; --accent:#D8FF3E; --accent2:#7C4DFF;
  --f-display:'Syne',sans-serif; --f-body:'IBM Plex Mono',monospace;
  --bd:1px; --rad:0px; --shadow:0 0 44px rgba(216,255,62,.22); --noise-op:.1;
}
body[data-theme="candy"]{
  --bg:#FFD1E3; --ink:#2B0A3D; --paper:#FFF6FB; --accent:#FFE14D; --accent2:#FF6EC7;
  --f-display:'Unbounded',sans-serif; --f-body:'Space Grotesk',sans-serif;
  --bd:3px; --rad:26px; --shadow:8px 8px 0 var(--ink); --noise-op:.05;
}
::selection{background:var(--accent2);color:var(--bg)}
img{display:block;max-width:100%}
a{color:inherit;text-decoration:none}
ul{list-style:none}
button{font:inherit;color:inherit;background:none;border:none;cursor:pointer}
:focus-visible{outline:3px dashed var(--accent2);outline-offset:3px}
.wrap{max-width:var(--wrap);margin:0 auto;padding-inline:var(--pad)}
.outl{color:transparent;-webkit-text-stroke:2px var(--ink)}

/* шум */
.noise{position:fixed;inset:-50%;z-index:60;pointer-events:none;opacity:var(--noise-op);
  background:url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300"><filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2"/></filter><rect width="300" height="300" filter="url(%23n)" opacity="0.7"/></svg>');
  animation:grain 8s steps(10) infinite}
@keyframes grain{0%,100%{transform:translate(0,0)}20%{transform:translate(-4%,3%)}40%{transform:translate(3%,-5%)}60%{transform:translate(-5%,-2%)}80%{transform:translate(4%,4%)}}

/* курсор */
.cursor,.cursor-ring{position:fixed;top:0;left:0;z-index:300;pointer-events:none;border-radius:50%;transform:translate(-50%,-50%);display:none}
.cursor{width:10px;height:10px;background:var(--accent2)}
.cursor-ring{width:38px;height:38px;border:2px solid var(--ink);transition:width .25s,height .25s,border-color .25s}
html.has-cursor .cursor,html.has-cursor .cursor-ring{display:block}
html.has-cursor .cursor-ring.big{width:64px;height:64px;border-color:var(--accent2)}
html.has-cursor, html.has-cursor a, html.has-cursor button{cursor:none}

/* ============ ШАПКА ============ */
header{position:fixed;inset:0 0 auto 0;z-index:100;height:78px;display:flex;align-items:center;gap:20px;
  padding-inline:var(--pad);border-bottom:var(--bd) solid transparent;transition:background .4s,border-color .4s}
header.scrolled{background:var(--bg);border-color:var(--ink)}
.logo{font-family:var(--f-display);font-size:22px;letter-spacing:.02em;display:inline-flex;align-items:center;transition:transform .2s}
.logo:hover{transform:rotate(-3deg) scale(1.05)}
.logo .logo-mark{font-size:.5em;vertical-align:top}
body[data-theme="brutal"] .logo{background:var(--ink);color:var(--accent);padding:6px 12px;box-shadow:4px 4px 0 var(--accent2)}
body[data-theme="luxe"] .logo{font-weight:600;letter-spacing:.28em}
body[data-theme="luxe"] .logo::before{content:"✦ ";color:var(--accent)}
body[data-theme="acid"] .logo{font-family:var(--f-mono);color:var(--accent)}
body[data-theme="acid"] .logo::before{content:"["}body[data-theme="acid"] .logo::after{content:"]"}
body[data-theme="candy"] .logo{background:var(--ink);color:var(--paper);padding:6px 16px;border-radius:999px;box-shadow:3px 3px 0 var(--accent2)}
.nav{display:flex;gap:26px;margin-left:auto}
.nav a{font-family:var(--f-mono);font-size:12px;letter-spacing:.2em;text-transform:uppercase;position:relative}
.nav a::after{content:"";position:absolute;left:0;bottom:-4px;width:100%;height:2px;background:var(--accent2);transform:scaleX(0);transform-origin:right;transition:transform .3s}
.nav a:hover::after{transform:scaleX(1);transform-origin:left}
.switcher{display:flex;gap:6px;margin-left:16px;border:var(--bd) solid var(--ink);padding:5px;border-radius:var(--rad);background:var(--paper)}
body[data-theme="luxe"] .switcher{background:transparent}
.sw{display:inline-flex;align-items:center;gap:7px;padding:6px 10px;font-family:var(--f-mono);font-size:11px;letter-spacing:.12em;border-radius:var(--rad);transition:background .25s,color .25s}
.sw .dot{width:12px;height:12px;border-radius:50%;border:2px solid var(--ink);flex:none}
.d-brutal{background:#FFD500}.d-luxe{background:#B08D3E}.d-acid{background:#D8FF3E}.d-candy{background:#FF6EC7}
.sw.active{background:var(--ink);color:var(--bg)}
.burger{display:none;margin-left:auto;width:46px;height:46px;position:relative;z-index:160;border:var(--bd) solid var(--ink);border-radius:var(--rad);background:var(--accent)}
.burger span{position:absolute;left:11px;right:11px;height:3px;background:var(--ink);transition:transform .35s,top .35s}
.burger span:nth-child(1){top:16px}.burger span:nth-child(2){top:26px}
body.menu-open .burger span:nth-child(1){top:21px;transform:rotate(45deg)}
body.menu-open .burger span:nth-child(2){top:21px;transform:rotate(-45deg)}

/* меню-оверлей */
.menu{position:fixed;inset:0;z-index:150;background:var(--bg);display:flex;flex-direction:column;justify-content:center;padding:100px var(--pad) 40px;transform:translateY(-102%);transition:transform .6s cubic-bezier(.77,0,.18,1)}
body.menu-open .menu{transform:none}
.menu a.m-link{font-family:var(--f-display);font-size:clamp(2rem,9vw,4.6rem);text-transform:uppercase;line-height:1.25;display:flex;gap:16px;align-items:baseline;opacity:0;transform:translateY(24px);transition:opacity .5s,transform .5s}
body.menu-open .menu a.m-link{opacity:1;transform:none}
.menu a.m-link:nth-child(2){transition-delay:.08s}.menu a.m-link:nth-child(3){transition-delay:.16s}.menu a.m-link:nth-child(4){transition-delay:.24s}
.menu a.m-link i{font-family:var(--f-mono);font-style:normal;font-size:12px;color:var(--accent2)}
.menu-foot{margin-top:auto;font-family:var(--f-mono);font-size:12px;opacity:.7;display:flex;gap:24px;flex-wrap:wrap}

/* ============ HERO ============ */
.hero{position:relative;padding:150px 0 70px;overflow:hidden}
.hero-ghost{position:absolute;top:4%;right:-3%;z-index:0;font-family:var(--f-display);font-size:clamp(220px,34vw,560px);line-height:.8;color:transparent;-webkit-text-stroke:2px var(--ink);opacity:.07;pointer-events:none;user-select:none}
.hero-inner{position:relative;z-index:1;display:grid;grid-template-columns:1.15fr .85fr;gap:clamp(28px,4vw,64px);align-items:center}
.hero-kicker{font-family:var(--f-mono);font-size:12px;letter-spacing:.3em;text-transform:uppercase;margin-bottom:18px}
.hero-kicker::before{content:"● ";color:var(--accent2)}
.hero-title{display:flex;flex-direction:column;line-height:.88}
.ht-bst{font-family:var(--f-display);font-weight:900;font-size:clamp(88px,15vw,230px);letter-spacing:-.01em}
.ht-bst sup{font-size:.16em;vertical-align:super}
.ht-word{font-family:var(--f-display);font-weight:800;font-size:clamp(44px,7.4vw,116px);color:transparent;-webkit-text-stroke:2px var(--ink);text-transform:uppercase}
body[data-theme="luxe"] .ht-word{-webkit-text-stroke:1.5px var(--accent);font-style:italic}
body[data-theme="acid"] .ht-word{-webkit-text-stroke:1.5px var(--accent)}
body[data-theme="candy"] .ht-word{color:var(--accent2);-webkit-text-stroke:3px var(--ink)}
.hero-tag{margin-top:22px;max-width:46ch;font-size:clamp(1rem,1.4vw,1.2rem);opacity:.85;transition:opacity .3s,transform .3s}
.hero-tag.swap{opacity:0;transform:translateY(8px)}
.hero-actions{display:flex;gap:14px;flex-wrap:wrap;margin-top:30px}
.btn{display:inline-flex;align-items:center;gap:10px;padding:16px 28px;border:var(--bd) solid var(--ink);border-radius:var(--rad);font-weight:700;font-size:14px;letter-spacing:.12em;text-transform:uppercase;transition:transform .2s,box-shadow .2s,background .2s,color .2s}
.btn-solid{background:var(--accent)}
.btn-line{background:transparent}
body[data-theme="brutal"] .btn{box-shadow:6px 6px 0 var(--ink)}
body[data-theme="brutal"] .btn:hover{transform:translate(-3px,-3px);box-shadow:10px 10px 0 var(--ink)}
body[data-theme="luxe"] .btn{letter-spacing:.28em;font-weight:500;padding:16px 34px}
body[data-theme="luxe"] .btn:hover{background:var(--ink);color:var(--bg)}
body[data-theme="acid"] .btn{border-color:var(--accent);color:var(--accent);box-shadow:0 0 22px rgba(216,255,62,.25)}
body[data-theme="acid"] .btn-solid{background:var(--accent);color:#0A0A0F}
body[data-theme="acid"] .btn:hover{box-shadow:0 0 40px rgba(216,255,62,.5);transform:translateY(-2px)}
body[data-theme="candy"] .btn{border-radius:999px;box-shadow:4px 4px 0 var(--ink)}
body[data-theme="candy"] .btn:hover{transform:translate(-2px,-2px);box-shadow:7px 7px 0 var(--ink)}
.hero-chips{display:flex;gap:10px;flex-wrap:wrap;margin-top:34px}
.hero-chips li{border:var(--bd) solid var(--ink);border-radius:var(--rad);padding:7px 14px;font-family:var(--f-mono);font-size:12px;background:var(--paper)}
body[data-theme="candy"] .hero-chips li{border-radius:999px}
body[data-theme="acid"] .hero-chips li{background:transparent;border-color:var(--accent2)}
.hero-frame{position:relative;border:var(--bd) solid var(--ink);border-radius:var(--rad);background:var(--paper);padding:10px;box-shadow:var(--shadow)}
body[data-theme="luxe"] .hero-frame{padding:16px}
body[data-theme="candy"] .hero-frame{border-radius:34px}
.hero-frame img{width:100%;aspect-ratio:4/5;object-fit:cover;border-radius:calc(var(--rad) * .6)}
.badge-spin{position:absolute;top:-52px;left:-52px;width:124px;height:124px;border-radius:50%;background:var(--accent);border:var(--bd) solid var(--ink);display:grid;place-items:center;animation:spin 14s linear infinite;z-index:2}
body[data-theme="candy"] .badge-spin{background:var(--accent)}
@keyframes spin{to{transform:rotate(360deg)}}
.badge-spin svg{width:100%;height:100%}
.badge-spin text{font-family:var(--f-mono);font-size:11.5px;letter-spacing:.18em;fill:var(--ink)}
.sticker{position:absolute;font-family:var(--f-display);font-size:14px;padding:8px 14px;border:var(--bd) solid var(--ink);z-index:2}
.st-1{right:-16px;top:26%;transform:rotate(8deg);background:var(--accent2);color:var(--bg)}
body[data-theme="luxe"] .st-1{color:var(--bg)}
.st-2{left:-14px;bottom:14%;transform:rotate(-6deg);background:var(--paper)}

/* ============ MARQUEE ============ */
.marquee{overflow:hidden;border-block:var(--bd) solid var(--ink);background:var(--accent);color:var(--ink);padding:14px 0;transition:background .5s}
body[data-theme="acid"] .marquee{transform:rotate(-1.2deg) scale(1.02)}
.marquee-track{display:flex;width:max-content;animation:mq 28s linear infinite}
.marquee-track span{white-space:pre;font-family:var(--f-display);font-size:clamp(16px,2.2vw,26px);text-transform:uppercase;letter-spacing:.06em}
@keyframes mq{to{transform:translateX(-50%)}}

/* ============ СЕКЦИИ ============ */
.section{padding-block:clamp(80px,10vw,140px)}
.sec-head{display:flex;justify-content:space-between;align-items:flex-end;gap:20px;margin-bottom:clamp(30px,4vw,56px);flex-wrap:wrap}
.sec-label{font-family:var(--f-mono);font-size:12px;letter-spacing:.3em;text-transform:uppercase}
.sec-label::before{content:"◼ ";color:var(--accent2)}
.sec-title{font-family:var(--f-display);font-weight:800;font-size:clamp(2rem,5.4vw,4.2rem);text-transform:uppercase;line-height:1}

/* манифест */
.manifesto{font-family:var(--f-display);font-weight:700;font-size:clamp(1.5rem,3.6vw,3rem);line-height:1.2;max-width:22em}
body[data-theme="luxe"] .manifesto{font-weight:600}
.manifesto em{font-style:normal;background:var(--accent);padding:.05em .25em;box-decoration-break:clone;-webkit-box-decoration-break:clone}
body[data-theme="luxe"] .manifesto em{font-style:italic;background:none;color:var(--accent);padding:0}
body[data-theme="acid"] .manifesto em{background:none;color:var(--accent);text-shadow:0 0 18px rgba(216,255,62,.6)}
body[data-theme="candy"] .manifesto em{border-radius:14px;background:var(--accent)}
.principles{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:14px;margin-top:clamp(36px,4vw,60px)}
.principles li{border:var(--bd) solid var(--ink);border-radius:var(--rad);background:var(--paper);padding:20px;transition:transform .25s,box-shadow .25s}
.principles li:hover{transform:translate(-3px,-3px);box-shadow:6px 6px 0 var(--accent2)}
body[data-theme="candy"] .principles li{border-radius:22px}
.principles b{font-family:var(--f-display);display:block;margin-bottom:8px;font-size:15px}
.principles small{font-family:var(--f-mono);font-size:12px;opacity:.7}

/* кейсы */
.cases-grid{display:grid;grid-template-columns:repeat(12,1fr);gap:clamp(18px,2.4vw,32px)}
.case.c1{grid-column:span 7}.case.c2{grid-column:span 5;margin-top:56px}
.case.c3{grid-column:span 5}.case.c4{grid-column:span 7;margin-top:56px}
.case-media{display:block;position:relative;overflow:hidden;border:var(--bd) solid var(--ink);border-radius:var(--rad);background:var(--paper)}
body[data-theme="candy"] .case-media{border-radius:26px}
.case-media::after{content:"";position:absolute;inset:0;background:var(--accent);mix-blend-mode:multiply;opacity:.16;transition:opacity .4s;pointer-events:none}
.case:hover .case-media::after{opacity:0}
.case-media img{width:100%;height:100%;aspect-ratio:16/11;object-fit:cover;transition:transform .9s cubic-bezier(.2,.7,.2,1)}
.case:hover .case-media img{transform:scale(1.07) rotate(.6deg)}
body[data-theme="brutal"] .case-media img{filter:grayscale(1) contrast(1.15)}
body[data-theme="luxe"] .case-media img{filter:sepia(.28) contrast(1.02)}
body[data-theme="acid"] .case-media img{filter:saturate(1.35) hue-rotate(-12deg) contrast(1.1)}
body[data-theme="candy"] .case-media img{filter:saturate(1.4) brightness(1.05)}
.case-meta{display:flex;justify-content:space-between;align-items:baseline;gap:12px;margin-top:14px}
.case-title{font-family:var(--f-display);font-size:clamp(1.1rem,2vw,1.5rem);text-transform:uppercase;display:flex;gap:12px;align-items:baseline}
.case-idx{font-family:var(--f-mono);font-style:normal;font-size:12px;color:var(--accent2)}
.case-tags{font-family:var(--f-mono);font-size:12px;opacity:.7;text-align:right}

/* услуги */
.svc-list{border-bottom:var(--bd) solid var(--ink)}
.svc-row{display:grid;grid-template-columns:90px 1fr auto 50px;align-items:center;gap:20px;padding:26px 10px;border-top:var(--bd) solid var(--ink);transition:background .3s,color .3s,padding .3s}
.svc-row:hover{background:var(--ink);color:var(--bg);padding-inline:26px}
body[data-theme="acid"] .svc-row:hover{background:var(--paper)}
.svc-idx{font-family:var(--f-mono);font-size:13px;color:var(--accent2)}
.svc-row:hover .svc-idx{color:var(--accent)}
.svc-name{font-family:var(--f-display);font-size:clamp(1.3rem,3vw,2.3rem);text-transform:uppercase}
.svc-desc{font-family:var(--f-mono);font-size:12px;opacity:.65;max-width:42ch}
.svc-arrow{font-size:26px;transition:transform .3s;text-align:right}
.svc-row:hover .svc-arrow{transform:rotate(45deg)}
.svc-preview{position:fixed;top:0;left:0;width:280px;height:200px;z-index:90;pointer-events:none;border:var(--bd) solid var(--ink);background:var(--paper);opacity:0;transform:scale(.8);transition:opacity .25s,transform .25s;overflow:hidden}
.svc-preview.on{opacity:1;transform:scale(1)}
.svc-preview img{width:100%;height:100%;object-fit:cover}

/* цифры */
.stats{background:var(--ink);color:var(--bg);border-block:var(--bd) solid var(--ink);display:grid;grid-template-columns:repeat(4,1fr)}
.stat{padding:clamp(30px,4vw,56px) clamp(20px,2.5vw,40px);border-left:var(--bd) solid var(--bg)}
.stat:first-child{border-left:none}
.stat .num{font-family:var(--f-display);font-size:clamp(2.6rem,5.6vw,5.2rem);color:var(--accent);display:block;line-height:1}
.stat p{font-family:var(--f-mono);font-size:12px;letter-spacing:.18em;text-transform:uppercase;opacity:.75;margin-top:10px}

/* мудборд */
.collage{display:grid;grid-template-columns:repeat(12,1fr);gap:clamp(16px,2vw,28px)}
.g-item{border:var(--bd) solid var(--ink);border-radius:var(--rad);overflow:hidden;position:relative;background:var(--paper)}
body[data-theme="candy"] .g-item{border-radius:24px}
.g-item img{width:100%;height:100%;object-fit:cover}
.g-item figcaption{position:absolute;left:0;bottom:0;background:var(--ink);color:var(--bg);font-family:var(--f-mono);font-size:11px;padding:6px 12px}
.g1{grid-column:span 5;--rot:-2deg;transform:rotate(var(--rot))}
.g2{grid-column:span 4;margin-top:52px;--rot:1.5deg;transform:rotate(var(--rot))}
.g3{grid-column:span 3;margin-top:110px;--rot:-1deg;transform:rotate(var(--rot))}
.g4{grid-column:span 7;margin-top:-40px;--rot:1deg;transform:rotate(var(--rot))}
.g5{grid-column:span 5;margin-top:20px;--rot:-1.5deg;transform:rotate(var(--rot))}

/* цитаты */
.quotes{display:grid;grid-template-columns:repeat(3,1fr);gap:clamp(16px,2vw,28px)}
.quote{border:var(--bd) solid var(--ink);border-radius:var(--rad);background:var(--paper);padding:30px 26px;position:relative;transition:transform .25s,box-shadow .25s}
body[data-theme="candy"] .quote{border-radius:26px}
.quote:hover{transform:translate(-3px,-3px);box-shadow:7px 7px 0 var(--accent2)}
.quote::before{content:"«";font-family:var(--f-display);font-size:64px;line-height:.6;color:var(--accent2);display:block;margin-bottom:18px}
.quote p{font-size:1.05rem;line-height:1.5}
.quote footer{margin-top:18px;font-family:var(--f-mono);font-size:12px;opacity:.7}

/* CTA */
.cta{background:var(--accent);color:var(--ink);border-block:var(--bd) solid var(--ink);padding:clamp(80px,10vw,140px) 0;position:relative;overflow:hidden}
body[data-theme="acid"] .cta{color:var(--accent)}
.cta-title{font-family:var(--f-display);font-weight:900;font-size:clamp(2.6rem,9vw,7.6rem);line-height:.92;text-transform:uppercase}
.cta-title span{display:block}
.cta-title .l2{color:transparent;-webkit-text-stroke:2px currentColor}
.cta .btn{margin-top:40px;background:var(--ink);color:var(--accent);font-size:16px;padding:20px 40px}
.cta-note{margin-top:18px;font-family:var(--f-mono);font-size:12px;opacity:.7}

/* футер */
footer.site{padding:60px 0 28px}
.foot-giant{font-family:var(--f-display);font-weight:900;font-size:clamp(110px,23vw,400px);line-height:.82;text-align:center;color:transparent;-webkit-text-stroke:2px var(--ink);transition:color .4s;user-select:none}
.foot-giant:hover{color:var(--accent)}
.foot-cols{display:grid;grid-template-columns:2fr 1fr 1fr 1fr;gap:28px;margin-top:clamp(36px,5vw,70px)}
.foot-cols h4{font-family:var(--f-mono);font-size:11px;letter-spacing:.28em;text-transform:uppercase;margin-bottom:14px;color:var(--accent2)}
.foot-cols a,.foot-cols p{display:block;font-size:14px;opacity:.85;margin-bottom:8px}
.foot-cols a:hover{opacity:1;color:var(--accent2)}
.credits{display:flex;justify-content:space-between;gap:16px;flex-wrap:wrap;border-top:var(--bd) solid var(--ink);margin-top:44px;padding-top:18px;font-family:var(--f-mono);font-size:11px;opacity:.65}

/* reveal */
.reveal{opacity:0;transform:translateY(34px);transition:opacity .8s cubic-bezier(.2,.7,.2,1),transform .8s cubic-bezier(.2,.7,.2,1)}
.reveal.in{opacity:1;transform:none}
.g-item.reveal.in{transform:rotate(var(--rot,0deg))}

/* ============ АДАПТИВ ============ */
@media (max-width:1100px){
  .nav{display:none}
  .burger{display:block}
  .switcher{margin-left:auto}
  .burger{margin-left:0}
}
@media (max-width:960px){
  .hero-inner{grid-template-columns:1fr}
  .hero-frame{max-width:560px}
  .case.c1,.case.c2,.case.c3,.case.c4{grid-column:span 12;margin-top:0}
  .stats{grid-template-columns:repeat(2,1fr)}
  .stat:nth-child(3){border-left:none}
  .stat{border-top:var(--bd) solid var(--bg)}
  .stat:nth-child(-n+2){border-top:none}
  .quotes{grid-template-columns:1fr}
  .g1,.g2,.g3,.g4,.g5{grid-column:span 6;margin-top:0}
  .foot-cols{grid-template-columns:1fr 1fr}
}
@media (max-width:700px){
  .sw span{display:none}
  .sw{padding:6px}
  .svc-row{grid-template-columns:44px 1fr 34px;grid-template-rows:auto auto}
  .svc-desc{grid-column:2/4}
  .badge-spin{width:96px;height:96px;top:-38px;left:-38px}
}
@media (max-width:560px){
  .g1,.g2,.g3,.g4,.g5{grid-column:span 12}
  .hero{padding-top:120px}
  .st-1{right:0}.st-2{left:0}
  .foot-cols{grid-template-columns:1fr}
}

/* reduced motion */
@media (prefers-reduced-motion: reduce){
  html{scroll-behavior:auto}
  *,*::before,*::after{animation:none!important;transition:none!important}
  .reveal{opacity:1;transform:none}
  .marquee-track{animation:none!important}
}
`

// ── HTML body ─────────────────────────────────────────────────────────────────
const MAXIMAL_LANDING_HTML_BODY = `
<div class="noise" aria-hidden="true"></div>
<div class="cursor" id="cursor" aria-hidden="true"></div>
<div class="cursor-ring" id="cursorRing" aria-hidden="true"></div>

<header id="header">
  <a class="logo" href="#top" aria-label="BST — на главную">BST<span class="logo-mark">®</span></a>
  <nav class="nav" aria-label="Основная навигация">
    <a href="#manifest">Манифест</a>
    <a href="#cases">Кейсы</a>
    <a href="#services">Услуги</a>
    <a href="#contact">Контакт</a>
  </nav>
  <div class="switcher" role="group" aria-label="Вариант бренда — 4 ДНК в одном коде">
    <button class="sw active" data-set-theme="brutal" aria-label="Вариант: нео-брутализм"><i class="dot d-brutal"></i><span>БРУТАЛ</span></button>
    <button class="sw" data-set-theme="luxe" aria-label="Вариант: ателье люкс"><i class="dot d-luxe"></i><span>АТЕЛЬЕ</span></button>
    <button class="sw" data-set-theme="acid" aria-label="Вариант: кислотный рейв"><i class="dot d-acid"></i><span>КИСЛОТА</span></button>
    <button class="sw" data-set-theme="candy" aria-label="Вариант: конфетный поп"><i class="dot d-candy"></i><span>ПОП</span></button>
  </div>
  <button class="burger" id="burger" aria-label="Открыть меню" aria-expanded="false"><span></span><span></span></button>
</header>

<div class="menu" id="menu">
  <a class="m-link" href="#manifest"><i>01</i>Манифест</a>
  <a class="m-link" href="#cases"><i>02</i>Кейсы</a>
  <a class="m-link" href="#services"><i>03</i>Услуги</a>
  <a class="m-link" href="#contact"><i>04</i>Контакт</a>
  <div class="menu-foot"><span>hello@bst.studio</span><span>SPB — MSK — DXB</span><span>© 2025</span></div>
</div>

<main id="top">

  <!-- HERO -->
  <section class="hero">
    <div class="hero-ghost" aria-hidden="true">BST</div>
    <div class="wrap hero-inner">
      <div class="hero-copy">
        <p class="hero-kicker" id="htKicker">ДИЗАЙН-СТУДИЯ × БРЕНДИНГ</p>
        <h1 class="hero-title">
          <span class="ht-bst">BST<sup>®</sup></span>
          <span class="ht-word" id="htWord">ГРОМКО</span>
        </h1>
        <p class="hero-tag" id="htTag">Максимализм без компромиссов: жирные шрифты, кислотные плашки и сетка, которая ломается специально.</p>
        <div class="hero-actions">
          <a class="btn btn-solid" href="#cases">Смотреть кейсы ↓</a>
          <a class="btn btn-line" href="#contact">Обсудить проект</a>
        </div>
        <ul class="hero-chips">
          <li>120+ проектов</li><li>Awwwards ×3</li><li>4 ДНК / 1 код</li><li>SPB—MSK—DXB</li>
        </ul>
      </div>
      <figure class="hero-frame">
        <div class="badge-spin" aria-hidden="true">
          <svg viewBox="0 0 120 120"><defs><path id="circ" d="M60,60 m-44,0 a44,44 0 1,1 88,0 a44,44 0 1,1 -88,0"/></defs>
          <text><textPath href="#circ">BST STUDIO • МАКСИМАЛИЗМ • BST STUDIO • </textPath></text></svg>
        </div>
        <img id="heroImg" src="https://image.qwenlm.ai/public_source/534e1a4a-0cc4-46b7-8d6a-a682a1df9cb9/124e8c17c-3755-40db-a2e8-2edbdfb52af3.png" alt="BST — текущий визуальный стиль" width="800" height="1000">
        <div class="sticker st-1" id="st1">MAX</div>
        <div class="sticker st-2" id="st2">100% ШУМ</div>
      </figure>
    </div>
  </section>

  <!-- MARQUEE -->
  <div class="marquee" aria-hidden="true"><div class="marquee-track" id="marqueeTrack"></div></div>

  <!-- МАНИФЕСТ -->
  <section class="section" id="manifest">
    <div class="wrap">
      <div class="sec-head reveal">
        <p class="sec-label">01 / Манифест</p>
        <p class="sec-label">EST. 2016</p>
      </div>
      <p class="manifesto reveal" data-t="maniText">Мы верим, что <em>тихих брендов</em> больше не существует. Лента съедает скромность за секунду — поэтому мы строим айдентику, которая <em>кричит</em>, интерфейс, который <em>танцует</em>, и код, который <em>не тормозит</em>.</p>
      <ul class="principles">
        <li class="reveal"><b data-t="princ1Title">Больше — это много</b><small data-t="princ1Desc">минимализм оставим другим</small></li>
        <li class="reveal"><b data-t="princ2Title">Сетка → разрыв</b><small data-t="princ2Desc">правила нужны, чтобы их ломать</small></li>
        <li class="reveal"><b data-t="princ3Title">Типографика = голос</b><small data-t="princ3Desc">шрифт громче логотипа</small></li>
        <li class="reveal"><b data-t="princ4Title">Движение = жизнь</b><small data-t="princ4Desc">60fps или ничего</small></li>
      </ul>
    </div>
  </section>

  <!-- КЕЙСЫ -->
  <section class="section" id="cases">
    <div class="wrap">
      <div class="sec-head reveal">
        <h2 class="sec-title">Кейсы <span class="outl">BST</span></h2>
        <p class="sec-label" id="casesCount">04 / избранных</p>
      </div>
      <div class="cases-grid" id="casesGrid"></div>
    </div>
  </section>

  <!-- УСЛУГИ -->
  <section class="section" id="services">
    <div class="wrap">
      <div class="sec-head reveal">
        <h2 class="sec-title">Что мы <span class="outl">делаем</span></h2>
        <p class="sec-label">03 / услуги</p>
      </div>
      <div class="svc-list">
        <a class="svc-row reveal" href="#contact" data-img="brutal"><span class="svc-idx">/01</span><span class="svc-name" data-t="svc1Title">Брендинг</span><span class="svc-desc" data-t="svc1Desc">логотипы · айдентика · гайдлайны · нейминг</span><span class="svc-arrow">→</span></a>
        <a class="svc-row reveal" href="#contact" data-img="luxe"><span class="svc-idx">/02</span><span class="svc-name" data-t="svc2Title">Веб-дизайн</span><span class="svc-desc" data-t="svc2Desc">лендинги · e-com · интерфейсы · дизайн-системы</span><span class="svc-arrow">→</span></a>
        <a class="svc-row reveal" href="#contact" data-img="acid"><span class="svc-idx">/03</span><span class="svc-name" data-t="svc3Title">Моушн и 3D</span><span class="svc-desc" data-t="svc3Desc">анимация · reels · WebGL · саунд-дизайн</span><span class="svc-arrow">→</span></a>
        <a class="svc-row reveal" href="#contact" data-img="candy"><span class="svc-idx">/04</span><span class="svc-name" data-t="svc4Title">Разработка</span><span class="svc-desc" data-t="svc4Desc">frontend · CMS · интеграции · 60fps</span><span class="svc-arrow">→</span></a>
        <a class="svc-row reveal" href="#contact" data-img="studio"><span class="svc-idx">/05</span><span class="svc-name" data-t="svc5Title">Стратегия</span><span class="svc-desc" data-t="svc5Desc">позиционирование · tone of voice · запуск</span><span class="svc-arrow">→</span></a>
      </div>
    </div>
    <div class="svc-preview" id="svcPreview" aria-hidden="true"><img id="svcPreviewImg" src="" alt=""></div>
  </section>

  <!-- ЦИФРЫ -->
  <section class="stats" aria-label="Студия в цифрах">
    <div class="stat reveal"><span class="num" data-count="120" data-suffix="+">0</span><p data-t="stat1L">проектов запущено</p></div>
    <div class="stat reveal"><span class="num" data-count="14" data-suffix="">0</span><p data-t="stat2L">международных наград</p></div>
    <div class="stat reveal"><span class="num" data-count="9" data-suffix="">0</span><p data-t="stat3L">лет громкого дизайна</p></div>
    <div class="stat reveal"><span class="num" data-count="27" data-suffix="">0</span><p data-t="stat4L">человек в команде</p></div>
  </section>

  <!-- МУДБОРД -->
  <section class="section" id="gallery">
    <div class="wrap">
      <div class="sec-head reveal">
        <h2 class="sec-title">Мудборд <span class="outl">студии</span></h2>
        <p class="sec-label">04 / из архива</p>
      </div>
      <div class="collage">
        <figure class="g-item g1 reveal" data-float="0.05"><img data-img="gal1img" loading="lazy" src="https://image.qwenlm.ai/public_source/534e1a4a-0cc4-46b7-8d6a-a682a1df9cb9/1e9006fab-ec4f-4096-8d8e-c7465c3bd63d.png" alt="Студия BST — idea lab"><figcaption data-t="gal1Cap">BST LAB / стены говорят</figcaption></figure>
        <figure class="g-item g2 reveal" data-float="-0.04"><img data-img="gal2img" loading="lazy" src="https://image.qwenlm.ai/public_source/534e1a4a-0cc4-46b7-8d6a-a682a1df9cb9/176005a8e-8ae9-4044-8e11-52a382e6b29c.png" alt="Коллаж из архива BST"><figcaption data-t="gal2Cap">zine vol.3 / бумага</figcaption></figure>
        <figure class="g-item g3 reveal" data-float="0.06"><img data-img="gal3img" loading="lazy" src="https://image.qwenlm.ai/public_source/534e1a4a-0cc4-46b7-8d6a-a682a1df9cb9/1a16c9d75-6bf4-4e57-b7ab-784952e10345.png" alt="Хром-логотип BST"><figcaption data-t="gal3Cap">chrome test / 3D</figcaption></figure>
        <figure class="g-item g4 reveal" data-float="-0.05"><img data-img="gal4img" loading="lazy" src="https://image.qwenlm.ai/public_source/534e1a4a-0cc4-46b7-8d6a-a682a1df9cb9/124e8c17c-3755-40db-a2e8-2edbdfb52af3.png" alt="Плакат BST в брутализме"><figcaption data-t="gal4Cap">poster / print run 500</figcaption></figure>
        <figure class="g-item g5 reveal" data-float="0.04"><img data-img="gal5img" loading="lazy" src="https://image.qwenlm.ai/public_source/534e1a4a-0cc4-46b7-8d6a-a682a1df9cb9/1f06b4788-d5d5-4024-a713-c207ce88f665.png" alt="Надувные буквы BST"><figcaption data-t="gal5Cap">balloon type / pop</figcaption></figure>
      </div>
    </div>
  </section>

  <!-- ЦИТАТЫ -->
  <section class="section" id="quotes">
    <div class="wrap">
      <div class="sec-head reveal">
        <h2 class="sec-title">Клиенты <span class="outl">о нас</span></h2>
        <p class="sec-label">05 / без ретуши</p>
      </div>
      <div class="quotes">
        <blockquote class="quote reveal"><p data-t="rev1Quote">BST сделали нам не сайт, а оружие продаж. Трафик вырос в 4 раза за квартал.</p><footer data-t="rev1Author">— Марина К., CMO «ЗАВОД»</footer></blockquote>
        <blockquote class="quote reveal"><p data-t="rev2Quote">Единственная студия, которая спорила с нами на каждой встрече. И была права.</p><footer data-t="rev2Author">— Артём Л., основатель NORD</footer></blockquote>
        <blockquote class="quote reveal"><p data-t="rev3Quote">Сроки, бюджет, вау-эффект — всё сошлось впервые за нашу историю.</p><footer data-t="rev3Author">— Дина С., brand-lead POP-MART</footer></blockquote>
      </div>
    </div>
  </section>

  <!-- CTA -->
  <section class="cta" id="contact">
    <div class="wrap">
      <h2 class="cta-title reveal">
        <span id="ctaL1">ДАВАЙТЕ</span>
        <span class="l2" id="ctaL2">ШУМЕТЬ</span>
        <span id="ctaL3">ВМЕСТЕ</span>
      </h2>
      <a class="btn reveal" href="mailto:hello@bst.studio">Оставить заявку ✦</a>
      <p class="cta-note reveal">ответим за 24 часа · NDA с первого письма · смета без сюрпризов</p>
    </div>
  </section>
</main>

<footer class="site">
  <div class="wrap">
    <div class="foot-giant" aria-hidden="true">BST</div>
    <div class="foot-cols">
      <div>
        <h4>О студии</h4>
        <p>BST — независимая студия максимализма. Один код, четыре визуальные ДНК: от нео-брутализма до конфетного попа.</p>
      </div>
      <div>
        <h4>Навигация</h4>
        <a href="#manifest">Манифест</a><a href="#cases">Кейсы</a><a href="#services">Услуги</a><a href="#contact">Контакт</a>
      </div>
      <div>
        <h4>Соцсети</h4>
        <a href="#" rel="noopener">Behance ↗</a><a href="#" rel="noopener">Instagram ↗</a><a href="#" rel="noopener">Telegram ↗</a><a href="#" rel="noopener">Dribbble ↗</a>
      </div>
      <div>
        <h4>Контакты</h4>
        <a href="mailto:hello@bst.studio">hello@bst.studio</a>
        <a href="tel:+78120000000">+7 812 000-00-00</a>
        <p>СПб — Мск — Дубай</p>
      </div>
    </div>
    <div class="credits">
      <span>© 2025 BST® — сделано громко</span>
      <span>4 бренда / 1 код / 0 шаблонов</span>
    </div>
  </div>
</footer>
`

// ── Engine JS ─────────────────────────────────────────────────────────────────
function maximalLandingEngineJs(brand: string, mode: 'canvas' | 'preview'): string {
  return `
(function(){
"use strict";
var RM = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
var FINE = window.matchMedia('(pointer: fine)').matches;

var IMG = {
  brutal:"https://image.qwenlm.ai/public_source/534e1a4a-0cc4-46b7-8d6a-a682a1df9cb9/124e8c17c-3755-40db-a2e8-2edbdfb52af3.png",
  luxe:"https://image.qwenlm.ai/public_source/534e1a4a-0cc4-46b7-8d6a-a682a1df9cb9/10a1878cf-ff98-48ba-8f02-3b93d93495f6.png",
  acid:"https://image.qwenlm.ai/public_source/534e1a4a-0cc4-46b7-8d6a-a682a1df9cb9/1a16c9d75-6bf4-4e57-b7ab-784952e10345.png",
  candy:"https://image.qwenlm.ai/public_source/534e1a4a-0cc4-46b7-8d6a-a682a1df9cb9/1f06b4788-d5d5-4024-a713-c207ce88f665.png",
  studio:"https://image.qwenlm.ai/public_source/534e1a4a-0cc4-46b7-8d6a-a682a1df9cb9/1e9006fab-ec4f-4096-8d8e-c7465c3bd63d.png",
  collage:"https://image.qwenlm.ai/public_source/534e1a4a-0cc4-46b7-8d6a-a682a1df9cb9/176005a8e-8ae9-4044-8e11-52a382e6b29c.png"
};

var THEMES = {
  brutal:{
    kicker:"ДИЗАЙН-СТУДИЯ × БРЕНДИНГ", word:"ГРОМКО",
    tag:"Максимализм без компромиссов: жирные шрифты, кислотные плашки и сетка, которая ломается специально.",
    marquee:"BST ✦ ДИЗАЙН ✦ БРЕНДИНГ ✦ МОУШН ✦ КОД ✦ ШУМ ✦ ",
    st1:"MAX", st2:"100% ШУМ", hero:IMG.brutal,
    cta:["ДАВАЙТЕ","ШУМЕТЬ","ВМЕСТЕ"],
    cases:[
      {t:"ZAVOD",   tags:"Айдентика / Веб — 2025", img:IMG.brutal},
      {t:"RAW MART",tags:"E-com / Моушн — 2024",   img:IMG.collage},
      {t:"КУЛЬТ",   tags:"Нейминг / Упаковка — 2024", img:IMG.studio},
      {t:"NOISE DEPT.", tags:"Веб / 3D — 2023",    img:IMG.acid}
    ]
  },
  luxe:{
    kicker:"ATELIER NUMÉRIQUE × EST. 2016", word:"ИЗЯЩНО",
    tag:"Ателье цифрового дизайна: выверенная типографика, воздух, дорогие детали и ни одного лишнего пикселя.",
    marquee:"BST ✦ ATELIER NUMÉRIQUE ✦ EST. 2016 ✦ HAUTE DESIGN ✦ ",
    st1:"№1", st2:"HAUTE", hero:IMG.luxe,
    cta:["СОЗДАДИМ","НЕЧТО","ВЫДАЮЩЕЕСЯ"],
    cases:[
      {t:"MAISON NORD", tags:"Брендинг / Print — 2025", img:IMG.luxe},
      {t:"OPERA SILK",  tags:"Веб / Арт-дирекшн — 2024", img:IMG.collage},
      {t:"GRAND EST",   tags:"Упаковка / Кампания — 2024", img:IMG.studio},
      {t:"ATELIER 7",   tags:"Айдентика — 2023", img:IMG.brutal}
    ]
  },
  acid:{
    kicker:"BST SYSTEM // ONLINE 24/7", word:"ЦИФРОВОЙ",
    tag:"Система BST онлайн: неон, глитч и интерфейсы, которые реагируют быстрее, чем ты успеваешь подумать.",
    marquee:"BST ▓ SYSTEM ONLINE ▓ TRANSMIT ▓ 24/7 ▓ ",
    st1:"24/7", st2:"RAVE", hero:IMG.acid,
    cta:["ПЕРЕДАЁМ","ВАШ","СИГНАЛ"],
    cases:[
      {t:"SIGNAL://24", tags:"WebGL / Моушн — 2025", img:IMG.acid},
      {t:"RAVE.LOG",    tags:"Айдентика / 3D — 2024", img:IMG.collage},
      {t:"NEON BAZA",   tags:"E-com / Кампания — 2024", img:IMG.studio},
      {t:"PULSE-9",     tags:"Веб / Саунд — 2023", img:IMG.brutal}
    ]
  },
  candy:{
    kicker:"ПОП-СТУДИЯ × ИГРАЕМ ВСЕРЬЁЗ", word:"СОЧНО",
    tag:"Игривый поп-максимализм: надувные формы, конфетные цвета и дизайн, который хочется лизнуть.",
    marquee:"BST ✦ СОЧНО ✦ ЯРКО ✦ ЗВОНКО ✦ СЛАДКО ✦ ",
    st1:"WOW", st2:"YUMMY", hero:IMG.candy,
    cta:["ХОТИТЕ","ТАК ЖЕ","СОЧНО?"],
    cases:[
      {t:"JUICE BOX",  tags:"Упаковка / 3D — 2025", img:IMG.candy},
      {t:"SUGAR CLUB", tags:"Брендинг / Веб — 2024", img:IMG.collage},
      {t:"POP-KIDS",   tags:"Кампания / Моушн — 2024", img:IMG.studio},
      {t:"BERRY BOOM", tags:"Айдентика — 2023", img:IMG.luxe}
    ]
  }
};

/* ---------- scramble ---------- */
function scramble(el, txt){
  if(!el) return;
  if(RM){ el.textContent = txt; return; }
  var chars = "█▓▒░#/<>*BST";
  var frame = 0, total = Math.max(14, txt.length * 2);
  if(el._si) clearInterval(el._si);
  el._si = setInterval(function(){
    frame++;
    var done = Math.floor((frame/total)*txt.length), out = "";
    for(var i=0;i<txt.length;i++){
      out += i < done ? txt[i] : (txt[i]===" " ? " " : chars[Math.floor(Math.random()*chars.length)]);
    }
    el.textContent = out;
    if(done >= txt.length) clearInterval(el._si);
  }, 30);
}

/* ---------- marquee ---------- */
var track = document.getElementById('marqueeTrack');
function setMarquee(text){
  var chunk = "";
  for(var i=0;i<4;i++) chunk += text;
  track.innerHTML = "<span>"+chunk+"</span><span aria-hidden='true'>"+chunk+"</span>";
}

/* ---------- reveal ---------- */
var io = new IntersectionObserver(function(entries){
  entries.forEach(function(e){
    if(e.isIntersecting){ e.target.classList.add('in'); io.unobserve(e.target); }
  });
},{threshold:.12});
function observeReveals(root){
  (root||document).querySelectorAll('.reveal:not(.in)').forEach(function(el){ io.observe(el); });
}

/* ---------- counters ---------- */
var cio = new IntersectionObserver(function(entries){
  entries.forEach(function(e){
    if(!e.isIntersecting) return;
    cio.unobserve(e.target);
    var el = e.target, target = +el.dataset.count, suf = el.dataset.suffix||"";
    if(RM){ el.textContent = target + suf; return; }
    var t0 = performance.now(), dur = 1400;
    (function tick(t){
      var p = Math.min(1,(t-t0)/dur), ease = 1-Math.pow(1-p,3);
      el.textContent = Math.round(target*ease) + suf;
      if(p<1) requestAnimationFrame(tick);
    })(t0);
  });
},{threshold:.5});
document.querySelectorAll('.num[data-count]').forEach(function(el){ cio.observe(el); });

/* ---------- кейсы ---------- */
var grid = document.getElementById('casesGrid');
function renderCases(cases){
  grid.innerHTML = cases.map(function(c,i){
    return "<article class='case c"+(i+1)+" reveal'>"
      + "<a class='case-media' href='#contact' aria-label='Кейс "+c.t+"'>"
      + "<img loading='lazy' src='"+c.img+"' alt='Кейс "+c.t+"'></a>"
      + "<div class='case-meta'><h3 class='case-title'><i class='case-idx'>0"+(i+1)+"</i>"+c.t+"</h3>"
      + "<p class='case-tags'>"+c.tags+"</p></div></article>";
  }).join("");
  observeReveals(grid);
}

/* ---------- смена темы ---------- */
var heroImg = document.getElementById('heroImg'),
    htTag = document.getElementById('htTag'),
    st1 = document.getElementById('st1'),
    st2 = document.getElementById('st2');
function applyTheme(id){
  var t = THEMES[id]; if(!t) return;
  document.body.dataset.theme = id;
  document.querySelectorAll('.sw').forEach(function(b){
    var on = b.dataset.setTheme === id;
    b.classList.toggle('active', on);
    b.setAttribute('aria-pressed', on);
  });
  scramble(document.getElementById('htKicker'), t.kicker);
  scramble(document.getElementById('htWord'), t.word);
  htTag.classList.add('swap');
  setTimeout(function(){ htTag.textContent = t.tag; htTag.classList.remove('swap'); }, RM?0:220);
  heroImg.src = t.hero;
  st1.textContent = t.st1; st2.textContent = t.st2;
  setMarquee(t.marquee);
  renderCases(t.cases);
  scramble(document.getElementById('ctaL1'), t.cta[0]);
  scramble(document.getElementById('ctaL2'), t.cta[1]);
  scramble(document.getElementById('ctaL3'), t.cta[2]);
}
document.querySelectorAll('.sw').forEach(function(b){
  b.addEventListener('click', function(){ applyTheme(b.dataset.setTheme); });
});

/* ---------- шапка / меню ---------- */
var header = document.getElementById('header');
window.addEventListener('scroll', function(){
  header.classList.toggle('scrolled', window.scrollY > 30);
},{passive:true});
var burger = document.getElementById('burger');
burger.addEventListener('click', function(){
  var open = document.body.classList.toggle('menu-open');
  burger.setAttribute('aria-expanded', open);
  burger.setAttribute('aria-label', open ? 'Закрыть меню' : 'Открыть меню');
});
document.querySelectorAll('.m-link').forEach(function(a){
  a.addEventListener('click', function(){ document.body.classList.remove('menu-open'); burger.setAttribute('aria-expanded','false'); });
});

/* ---------- preview услуг ---------- */
if(FINE && !RM){
  var preview = document.getElementById('svcPreview'),
      pImg = document.getElementById('svcPreviewImg'),
      svcSection = document.getElementById('services');
  document.querySelectorAll('.svc-row').forEach(function(row){
    row.addEventListener('mouseenter', function(){
      pImg.src = IMG[row.dataset.img] || "";
      preview.classList.add('on');
    });
    row.addEventListener('mouseleave', function(){ preview.classList.remove('on'); });
  });
  svcSection.addEventListener('mousemove', function(e){
    preview.style.left = 0; preview.style.top = 0;
    preview.style.transform = "translate("+(e.clientX+30)+"px,"+(e.clientY-100)+"px) scale(1)";
  });
}

/* ---------- параллакс коллажа ---------- */
var floats = Array.prototype.slice.call(document.querySelectorAll('[data-float]'));
if(!RM && floats.length){
  var ticking = false;
  function doFloat(){
    var vh = window.innerHeight;
    floats.forEach(function(el){
      var r = el.getBoundingClientRect();
      var off = (r.top + r.height/2 - vh/2) * parseFloat(el.dataset.float);
      el.style.marginTop = off + "px";
    });
    ticking = false;
  }
  window.addEventListener('scroll', function(){
    if(!ticking){ ticking = true; requestAnimationFrame(doFloat); }
  },{passive:true});
  doFloat();
}

/* ---------- курсор ---------- */
if(FINE && !RM){
  document.documentElement.classList.add('has-cursor');
  var cur = document.getElementById('cursor'), ring = document.getElementById('cursorRing');
  var mx = innerWidth/2, my = innerHeight/2, rx = mx, ry = my;
  window.addEventListener('mousemove', function(e){ mx = e.clientX; my = e.clientY; });
  (function loop(){
    rx += (mx-rx)*.14; ry += (my-ry)*.14;
    cur.style.transform = "translate("+mx+"px,"+my+"px) translate(-50%,-50%)";
    ring.style.transform = "translate("+rx+"px,"+ry+"px) translate(-50%,-50%)";
    requestAnimationFrame(loop);
  })();
  document.querySelectorAll('a,button,.svc-row').forEach(function(el){
    el.addEventListener('mouseenter', function(){ ring.classList.add('big'); });
    el.addEventListener('mouseleave', function(){ ring.classList.remove('big'); });
  });
}

/* ---------- старт ---------- */
observeReveals();
applyTheme('${brand}');
${mode === 'canvas' ? `(function(){
  document.querySelectorAll('.reveal,[data-rv],.rv').forEach(function(el){el.classList.add('in');});
  document.querySelectorAll('.scramble').forEach(function(el){el.textContent=el.dataset.final||el.textContent;});
  document.body.classList.add('loaded','is-ready');
  document.addEventListener('click',function(e){var a=e.target.closest('a[href]');if(a)e.preventDefault();},true);
  document.querySelectorAll('form').forEach(function(f){f.addEventListener('submit',function(e){e.preventDefault();});});
})();` : ''}
})();
`
}

// ── Public API ────────────────────────────────────────────────────────────────
export function getMaximalLandingPageHtml(data: MaximalLandingPageData, mode: 'canvas' | 'preview'): string {
  const brand = data.brand ?? 'brutal'
  return `<!DOCTYPE html>
<html lang="ru">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>BST® — студия громкого дизайна · 4 бренда в одном коде</title>
<meta name="description" content="BST — максимализм без компромиссов. Один код, четыре визуальных ДНК: нео-брутализм, ателье, кислота, поп.">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/archivo-black@latest/index.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/space-grotesk@latest/latin-400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/space-grotesk@latest/latin-500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/space-grotesk@latest/latin-700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/cormorant-garamond@latest/latin-400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/cormorant-garamond@latest/latin-600.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/cormorant-garamond@latest/latin-400-italic.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/syne@latest/latin-700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/syne@latest/latin-800.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/ibm-plex-mono@latest/latin-400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/ibm-plex-mono@latest/latin-500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/unbounded@latest/latin-700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/unbounded@latest/latin-900.css">
<style>${MAXIMAL_LANDING_CSS}</style>
</head>
<body data-theme="${brand}">
${MAXIMAL_LANDING_HTML_BODY}
<script>${maximalLandingEngineJs(brand, mode)}</script>
</body>
</html>`
}
